/**
 * スコア・コンボ・ゲージ・星・コインの計算(docs/02_game_design.md §5)。
 *
 * すべて純関数。定数は `config.ts` からのみ読む。
 */

import {
  GAUGE_MULTIPLIER_TABLE,
  PERFORMANCE_THRESHOLDS,
  SCORING,
  STAR_THRESHOLDS,
  getDifficultySettings,
} from './config.js';
import type { Difficulty, PerformanceStage, TypingLanguage } from './types.js';

/**
 * 基本スコア(docs/02 §5.2)。
 * `baseScore = 1 + ステージ内の累計正解打鍵数 × 0.1`。
 * Unity版に合わせ、**その打鍵を含めた**累計正解数で評価する(最初の1打は 1.1)。
 */
export function calcBaseScore(totalCorrect: number): number {
  const correct = Math.max(0, totalCorrect);
  return SCORING.baseScore + correct * SCORING.baseScorePerCorrect;
}

/**
 * コンボ倍率(docs/02 §5.3)。
 *
 * コンボ10未満は ×1.0。以降10コンボごとに `+(コンボ/10) × 0.1` を積み上げる。
 * 閉じた式にすると `1 + 0.1 × k(k+1)/2`(k = floor(コンボ/10))。
 * 10→1.1 / 20→1.3 / 30→1.6 / 40→2.0 / 50→2.5 / 60→3.1(上限3.0で頭打ち)。
 */
export function calcComboBonus(combo: number, cap: number = SCORING.comboBonusCap): number {
  const steps = Math.floor(Math.max(0, combo) / SCORING.comboStep);
  const raw = SCORING.comboBonusBase + SCORING.comboBonusUnit * ((steps * (steps + 1)) / 2);
  return Math.min(raw, cap);
}

/** ゲージ倍率(docs/02 §5.4)。 */
export function calcGaugeMultiplier(gauge: number): number {
  const value = clampGauge(gauge);
  for (const row of GAUGE_MULTIPLIER_TABLE) {
    if (value <= row.maxGauge) return row.multiplier;
  }
  /* c8 ignore next */
  return 1;
}

/** 演出段階(docs/02 §5.5)。 */
export function calcPerformanceStage(gauge: number): PerformanceStage {
  const value = clampGauge(gauge);
  if (value > PERFORMANCE_THRESHOLDS.fever) return 'fever';
  if (value > PERFORMANCE_THRESHOLDS.groove) return 'groove';
  return 'normal';
}

/** ゲージを 0〜100 に丸める。 */
export function clampGauge(gauge: number): number {
  if (!Number.isFinite(gauge)) return SCORING.gaugeMin;
  return Math.min(SCORING.gaugeMax, Math.max(SCORING.gaugeMin, gauge));
}

/** 正解打鍵によるゲージ増加。 */
export function gainGauge(gauge: number): number {
  return clampGauge(gauge + SCORING.gaugeGainPerCorrect);
}

/**
 * 時間経過によるゲージ減衰(docs/02 §5.4)。
 * `decayReduction` はアイテム効果(0〜1)。
 */
export function decayGauge(
  gauge: number,
  deltaMs: number,
  difficulty: Difficulty,
  decayReduction = 0,
): number {
  if (deltaMs <= 0) return clampGauge(gauge);
  const perMs = getDifficultySettings(difficulty).gaugeDecayPer100ms / 100;
  const rate = perMs * (1 - Math.min(1, Math.max(0, decayReduction)));
  return clampGauge(gauge - deltaMs * rate);
}

/** 1打鍵ぶんのスコア計算に必要な入力。 */
export interface KeyScoreInput {
  /** その打鍵を含むステージ内累計正解打鍵数。 */
  readonly totalCorrect: number;
  /** その打鍵を含む現在のコンボ数。 */
  readonly combo: number;
  /** その打鍵**後**のゲージ(%)。 */
  readonly gauge: number;
  readonly difficulty: Difficulty;
  /** コンボ倍率の上限(アイテム効果適用後)。 */
  readonly comboBonusCap?: number;
  /** その他のアイテム補正(将来拡張用。既定 1)。 */
  readonly itemMultiplier?: number;
}

/** 1打鍵ぶんのスコア内訳。 */
export interface KeyScoreBreakdown {
  readonly baseScore: number;
  readonly comboBonus: number;
  readonly gaugeBonus: number;
  readonly difficultyMultiplier: number;
  readonly itemMultiplier: number;
  /** 実際の加算値(整数。Unity版に合わせて切り捨て)。 */
  readonly gain: number;
}

/**
 * 1打鍵ぶんのスコア(docs/02 §5.1)。
 * `score = baseScore × comboBonus × gaugeBonus × 難易度倍率 × アイテム補正`
 */
export function calcKeyScore(input: KeyScoreInput): KeyScoreBreakdown {
  const baseScore = calcBaseScore(input.totalCorrect);
  const comboBonus = calcComboBonus(input.combo, input.comboBonusCap ?? SCORING.comboBonusCap);
  const gaugeBonus = calcGaugeMultiplier(input.gauge);
  const difficultyMultiplier = getDifficultySettings(input.difficulty).scoreMultiplier;
  const itemMultiplier = input.itemMultiplier ?? 1;
  const gain = Math.floor(
    baseScore * comboBonus * gaugeBonus * difficultyMultiplier * itemMultiplier,
  );
  return { baseScore, comboBonus, gaugeBonus, difficultyMultiplier, itemMultiplier, gain };
}

/** 打鍵速度(打/分)。 */
export function calcKpm(correctKeys: number, durationMs: number): number {
  if (durationMs <= 0) return 0;
  return (correctKeys * 60000) / durationMs;
}

/** ミス率(0〜1)。1打も打っていなければ 0。 */
export function calcMissRate(correctKeys: number, missKeys: number): number {
  const total = correctKeys + missKeys;
  if (total <= 0) return 0;
  return missKeys / total;
}

/** 星判定に必要な入力。 */
export interface StarInput {
  readonly cleared: boolean;
  readonly missRate: number;
  readonly kpm: number;
}

/**
 * 星の数(0〜5)(docs/02 §5.6)。累積条件で判定する。
 * ★1 クリア / ★2 ミス率<10% / ★3 ミス率<5% / ★4 ★3+60打分 / ★5 ミス率<3%+90打分。
 */
export function calcStars(input: StarInput): number {
  if (!input.cleared) return 0;
  let stars = 1;
  if (input.missRate < STAR_THRESHOLDS.star2MissRateBelow) stars = 2;
  if (stars >= 2 && input.missRate < STAR_THRESHOLDS.star3MissRateBelow) stars = 3;
  if (stars >= 3 && input.kpm >= STAR_THRESHOLDS.star4KpmAtLeast) stars = 4;
  if (
    stars >= 4 &&
    input.missRate < STAR_THRESHOLDS.star5MissRateBelow &&
    input.kpm >= STAR_THRESHOLDS.star5KpmAtLeast
  ) {
    stars = 5;
  }
  return Math.min(stars, STAR_THRESHOLDS.maxStars);
}

/**
 * 通常クリア時のコイン(docs/02 §5.7)。
 *
 * `min(floor(score × 言語別係数 × アイテム倍率), 上限)`。
 * 係数が言語別なので**言語は必ず呼び出し側が渡す**(既定値を置くと取り違えに気づけない)。
 * サーバー(`functions` の `coinsFromScore`)が正で、そちらは倍率を掛けない。
 */
export function calcClearCoins(
  score: number,
  language: TypingLanguage,
  coinMultiplier = 1,
): number {
  if (score <= 0) return 0;
  const coins = Math.floor(score * SCORING.coinRate[language] * coinMultiplier);
  return Math.min(coins, SCORING.coinMaxPerClear);
}

/**
 * 初回星獲得ボーナスコイン(docs/02 §5.7)。
 *
 * @param stars       今回の獲得星数
 * @param awardedStars すでに付与済みの星数(難易度別)
 * @param starCoins   星1〜5それぞれのボーナスコイン(ステージ定義の `starCoins`)
 */
export function calcStarBonusCoins(
  stars: number,
  awardedStars: number,
  starCoins: readonly number[],
  coinMultiplier = 1,
): number {
  let total = 0;
  for (let star = awardedStars + 1; star <= stars; star++) {
    total += starCoins[star - 1] ?? 0;
  }
  if (total <= 0) return 0;
  return Math.floor(total * coinMultiplier);
}
